/**
 * chat v1 sign-in — Cognito hosted UI, authorization-code + PKCE, federated to Google.
 *
 * The user is already in Google (the app's own session), so Cognito's authorize with
 * `identity_provider=Google` passes through silently — a quick redirect, no second consent.
 * The app client has NO secret (public client), so PKCE is the proof. Real Google tokens
 * carry `custom:ext_iss/ext_sub` (= the BFF account key, which is how friend-gated features
 * resolve); the dev admin token falls back to the Cognito `sub`.
 *
 * Flow: startChatv1Login() → Cognito → `${origin}/chat/callback?code` → handleChatv1Callback()
 * exchanges the code at /oauth2/token → holds the id_token (token.ts) + persists the refresh
 * token → refreshChatv1Token() renews silently thereafter.
 */
import { CHATV1 } from './config'
import { setChatv1Token, chatv1Token } from './token'

const REFRESH_KEY = 'av_chatv1_refresh'
const PKCE_KEY = 'av_chatv1_pkce'
const STATE_KEY = 'av_chatv1_state'
const RETURN_KEY = 'av_chatv1_return'
const CALLBACK_PATH = '/chat/callback'

function base64url(bytes: Uint8Array): string {
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomToken(): string {
  const b = new Uint8Array(32)
  crypto.getRandomValues(b)
  return base64url(b)
}

async function challengeOf(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64url(new Uint8Array(digest))
}

function redirectUri(): string {
  return `${location.origin}${CALLBACK_PATH}`
}

function claimsOf(jwt: string): Record<string, unknown> {
  try {
    const payload = jwt.split('.')[1]
    if (!payload) return {}
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>
  } catch {
    return {}
  }
}

/** Decode a JWT's `exp` (seconds). Returns 0 if unparseable. */
function expOf(jwt: string): number {
  const exp = claimsOf(jwt)['exp']
  return typeof exp === 'number' ? exp : 0
}

/**
 * This account's chat v1 user id, derived from the current id_token EXACTLY as the backend's
 * `userOf` does: `custom:ext_iss|custom:ext_sub` for a real federated Google account, else the
 * Cognito `sub`. This is the id a message's `sender` is compared against for the "mine" flag —
 * not the Google email. Empty string when there's no usable token.
 */
export function chatv1SelfUser(): string {
  const t = chatv1Token()
  if (!t) return ''
  const c = claimsOf(t)
  const iss = c['custom:ext_iss']
  const sub = c['custom:ext_sub']
  if (typeof iss === 'string' && iss && typeof sub === 'string' && sub) return `${iss}|${sub}`
  return typeof c['sub'] === 'string' ? (c['sub'] as string) : ''
}

/**
 * This account's email, from the id_token's `email` claim (the login scope includes
 * `email`). Used to light the panel's "you" badge on the viewer's own rows — it matches
 * the Google email the app already holds for a real federated account. Empty when there's
 * no usable token or no claim (e.g. the dev admin account, which has no profile email).
 */
export function chatv1SelfEmail(): string {
  const t = chatv1Token()
  if (!t) return ''
  const email = claimsOf(t)['email']
  return typeof email === 'string' ? email : ''
}

/** True if we have no token or it expires within the next 60s.
 *
 * Exported for the transport's PROACTIVE renewal: the chatv1 API Gateway answers
 * a MISSING token with 401 but an expired-yet-present one with 403 (avg-main
 * curl-measured both), and the transport's retry originally keyed on 401 alone —
 * so a token that aged out between visits sailed into a 403 nobody retried,
 * and the sidebar said "couldn't load projects: http_403" until a refresh. */
export function tokenStale(): boolean {
  const t = chatv1Token()
  if (!t) return true
  return expOf(t) - 60 <= Math.floor(Date.now() / 1000)
}

/** Begin sign-in — redirects the page to Cognito. Resolves only if the redirect can't start. */
export async function startChatv1Login(): Promise<void> {
  const verifier = randomToken()
  const state = randomToken()
  try {
    // localStorage, NOT sessionStorage: the Google→Cognito round-trip can land the
    // callback in a fresh browsing context on mobile (in-app browser hand-off, tab
    // recycling), where sessionStorage is empty — the PKCE verifier would be gone and
    // the code-exchange would silently fail with no refresh token ever persisted. That
    // was the account-with-no-machine "couldn't load projects / no_token" dead end.
    localStorage.setItem(PKCE_KEY, verifier)
    localStorage.setItem(STATE_KEY, state)
    // Come back to where we were, not to the app root, after the round-trip.
    localStorage.setItem(RETURN_KEY, location.pathname + location.search)
  } catch {
    /* without storage the callback can't verify — surfaced there as a failure */
  }
  const challenge = await challengeOf(verifier)
  const q = new URLSearchParams({
    client_id: CHATV1.clientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: redirectUri(),
    identity_provider: 'Google',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
  })
  location.assign(`${CHATV1.authDomain}/oauth2/authorize?${q.toString()}`)
}

/** True when the current URL is the OAuth redirect landing carrying a `code` (success). */
export function isChatv1Callback(): boolean {
  return location.pathname === CALLBACK_PATH && new URLSearchParams(location.search).has('code')
}

/** True when we're on the callback path AT ALL — success (`?code`) or failure (`?error`).
 *  Boot uses this to always tear down the one-shot login guard on return, so a federation
 *  that bounces back with `?error=` (or nothing) can't leave the tab permanently wedged. */
export function isChatv1CallbackPath(): boolean {
  return location.pathname === CALLBACK_PATH
}

/**
 * Handle the redirect landing: verify state, exchange the code for tokens, hold the id_token,
 * persist the refresh token. Returns true on success. Does NOT navigate — the caller cleans
 * the URL (so the code isn't left in history/logs).
 */
export async function handleChatv1Callback(): Promise<boolean> {
  const params = new URLSearchParams(location.search)
  const code = params.get('code')
  const state = params.get('state')
  let savedState: string | null = null
  let verifier: string | null = null
  try {
    savedState = localStorage.getItem(STATE_KEY)
    verifier = localStorage.getItem(PKCE_KEY)
    localStorage.removeItem(STATE_KEY)
    localStorage.removeItem(PKCE_KEY)
  } catch {
    /* no storage */
  }
  if (!code || !state || !savedState || state !== savedState || !verifier) return false
  try {
    const res = await fetch(`${CHATV1.authDomain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: CHATV1.clientId,
        code,
        redirect_uri: redirectUri(),
        code_verifier: verifier,
      }).toString(),
    })
    if (!res.ok) return false
    const tok = (await res.json()) as { id_token?: string; refresh_token?: string }
    if (!tok.id_token) return false
    setChatv1Token(tok.id_token)
    if (tok.refresh_token) {
      try {
        localStorage.setItem(REFRESH_KEY, tok.refresh_token)
      } catch {
        /* not persistable — token still valid for this session */
      }
    }
    return true
  } catch {
    return false
  }
}

/**
 * Obtain a chat id_token from the BFF's same-origin `/chat/token`, minted from the
 * app's own Google session (the one `/auth/me` already proves). Returns the token or
 * null.
 *
 * This REPLACED the Cognito refresh flow. The Cognito path needed a Google→Cognito
 * OAuth *redirect* to get the first token; that redirect cannot complete inside an
 * iOS home-screen PWA (the callback lands in a separate Safari context whose storage
 * the PWA never sees), so a machine-less account there could never obtain a token.
 * A same-origin fetch has no redirect and works in any PWA/webview. The name is kept
 * because every caller (bootstrap, ensureChatv1Token, the transport's no-token and
 * 401 paths) already means "get me a fresh token, quietly" — the source just moved.
 */
export async function refreshChatv1Token(): Promise<string | null> {
  try {
    // Under /auth/ because that prefix is already proxied to the BFF everywhere; a
    // bare /chat/* would fall through to the SPA and hand back HTML, not a token.
    const res = await fetch('/auth/chat_token', { credentials: 'include', cache: 'no-store' })
    if (!res.ok) return null
    const tok = (await res.json()) as { id_token?: string }
    if (!tok.id_token) return null
    setChatv1Token(tok.id_token)
    return tok.id_token
  } catch {
    return null
  }
}

/**
 * Ensure a usable id_token without a redirect if possible: the in-memory/dev-seed one if
 * fresh, else a silent refresh. Returns a token, or null when interactive login is required
 * (the caller then decides whether to `startChatv1Login()`).
 */
export async function ensureChatv1Token(): Promise<string | null> {
  if (!tokenStale()) return chatv1Token()
  const refreshed = await refreshChatv1Token()
  if (refreshed) return refreshed
  // a fresh dev-seed token that isn't stale would have returned above; nothing usable now
  return tokenStale() ? null : chatv1Token()
}

export function chatv1SignedOut(): void {
  setChatv1Token(null)
  try {
    localStorage.removeItem(REFRESH_KEY)
  } catch {
    /* ignore */
  }
}

/** The path to restore after a callback (where startChatv1Login was invoked from), or '/'. */
export function consumeChatv1Return(): string {
  try {
    const r = localStorage.getItem(RETURN_KEY)
    localStorage.removeItem(RETURN_KEY)
    if (r && r.startsWith('/')) return r
  } catch {
    /* no storage */
  }
  return '/'
}
